<template>
  <div>
    <div class="card">
      <div class="card-content">
        <div class="content">
          <b-field grouped>
            <b-field :label="$t('common.category')">
              <category-select
                v-model="filterCategory"
                :is-enabled-undo="false"
                :is-list-loaded="isLoaded"
              ></category-select>
            </b-field>
          </b-field>

          <p
            v-if="filterCategoryInputError"
            class="has-text-danger"
          >
            {{ filterCategoryInputError }}
          </p>

          <b-field
            class="mt-4"
            grouped
          >
            <b-field>
              <button
                class="button is-info"
                @click="executeFilter()"
                :disabled="hasFilterInputError"
              >
                {{ $t('common.run') }}
              </button>
            </b-field>
            <b-field>
              <button
                class="button is-light"
                @click="resetFilter()"
              >
                {{ $t('common.reset') }}
              </button>
            </b-field>
          </b-field>
        </div>
      </div>
    </div>

    <div>
      <table
        v-if="posts.length > 0"
        class="table is-fullwidth mt-6"
      >
        <thead>
          <tr>
            <th class="is-size-7 u-clickable is-underlined">
              <router-link :to="getUrlObjBySortOrder('title')">
                <span>{{ $t('form.title') }}</span>
                <span
                  v-if="sort === 'title'"
                  class="icon"
                >
                  <i :class="sortIconClass"></i>
                </span>
              </router-link>
            </th>
            <th class="is-size-7">{{ $t('common.category') }}</th>
            <th
              v-if="isAdmin"
              class="is-size-7"
            >
              {{ $t('common.edit') }}
            </th>
            <th
              v-if="isAdmin"
              class="is-size-7 u-clickable is-underlined"
            >
              <router-link :to="getUrlObjBySortOrder('createdAt')">
                <span>{{ $t('common.createdAt') }}</span>
                <span
                  v-if="sort === 'createdAt'"
                  class="icon"
                >
                  <i :class="sortIconClass"></i>
                </span>
              </router-link>
            </th>
            <th
              v-if="!isAdmin"
              class="master-col is-size-7"
            >
              {{ $t('term.isMastered') }}
            </th>
          </tr>
        </thead>
        <tfoot>
          <tr>
            <th class="is-size-7 u-clickable is-underlined">
              <router-link :to="getUrlObjBySortOrder('title')">
                <span>{{ $t('form.title') }}</span>
                <span
                  v-if="sort === 'title'"
                  class="icon"
                >
                  <i :class="sortIconClass"></i>
                </span>
              </router-link>
            </th>
            <th class="is-size-7">{{ $t('common.category') }}</th>
            <th
              v-if="isAdmin"
              class="is-size-7"
            >
              {{ $t('common.edit') }}
            </th>
            <th
              v-if="isAdmin"
              class="is-size-7 u-clickable is-underlined"
            >
              <router-link :to="getUrlObjBySortOrder('createdAt')">
                <span>{{ $t('common.createdAt') }}</span>
                <span
                  v-if="sort === 'createdAt'"
                  class="icon"
                >
                  <i :class="sortIconClass"></i>
                </span>
              </router-link>
            </th>
            <th
              v-if="!isAdmin"
              class="master-col is-size-7"
            >
              {{ $t('term.isMastered') }}
            </th>
          </tr>
        </tfoot>
        <tbody>
          <PostsTableRow
            v-for="post in posts"
            :key="post.slug"
            :post="post"
            :is-admin="isAdmin"
            :is-mastered="masteredPostIds.includes(post.postId)"
          />
        </tbody>
      </table>

      <div
        v-else
        class="mb-6"
      >
        <p class="mt-6">{{ $t('msg["Data is empty"]') }}</p>
      </div>

      <nav
        class="pagination"
        role="navigation"
        aria-label="pagination"
      >
        <router-link
          :to="getUrlObjByPageIndex(index - 1)"
          class="pagination-previous"
          :class="{ 'is-disabled': !existsPrev }"
        >
          <span class="icon">
            <i class="fas fa-angle-left"></i>
          </span>
          <span>{{ $t('common.toPrev') }}</span>
        </router-link>

        <router-link
          :to="getUrlObjByPageIndex(index + 1)"
          class="pagination-next"
          :class="{ 'is-disabled': !existsNext }"
        >
          <span class="icon">
            <i class="fas fa-angle-right"></i>
          </span>
          <span>{{ $t('common.toNext') }}</span>
        </router-link>

        <ul class="pagination-list">
          <li>
            <router-link
              :to="getUrlObjByPageIndex()"
              class="pagination-link"
              :class="{ 'is-disabled': !existsPrev }"
            >
              <span class="icon">
                <i class="fas fa-angle-double-left"></i>
              </span>
              <span>{{ $t('common.toFirst') }}</span>
            </router-link>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>
<script>
import utilStr from '@/util/str'
import utilObj from '@/util/obj'
import { Post, Category, User } from '@/api'
import PostsTableRow from '@/components/organisms/PostsTableRow'
import CategorySelect from '@/components/molecules/CategorySelect'

export default {
  name: 'AdminPostList',

  components: {
    PostsTableRow,
    CategorySelect,
  },

  props: {
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      posts: [],
      masteredPostIds: [],
      sortsAllowed: ['title', 'createdAt'],
      ordersAllowed: ['asc', 'desc'],
      filterCategory: '',
      filterCategoryInputError: '',
      requestedParams: {},
      isLoaded: false,
    }
  },

  computed: {
    // posts() {
    //   return this.$store.state.postList
    // },

    urlPrefix() {
      if (this.isAdmin) return '/admin'
      return ''
    },

    index() {
      return this.$route.query.index ? Number(this.$route.query.index) : 0
    },

    sort() {
      const defaultValue = 'title'
      const reqSort = this.$route.query.sort
      if (!reqSort) return defaultValue
      return reqSort
    },

    order() {
      const defaultValue = 'desc'
      const reqOrder = this.$route.query.order
      if (!reqOrder) return defaultValue
      return this.$route.query.order
    },

    filterCategoryQuery() {
      const defaultValue = ''
      if (!this.$route.query.category) return defaultValue
      return this.$route.query.category
    },

    filterCategoryInput() {
      const defaultValue = ''
      if (this.filterCategoryInputError) return defaultValue
      return this.filterCategory
    },

    filterCategoryForReq() {
      if (this.filterCategoryInput) return this.filterCategoryInput
      if (this.filterCategoryQuery) return this.filterCategoryQuery
      return ''
    },

    filters() {
      if (this.checkEmpty(this.filtersQuery) === false) return this.filtersQuery
      return null
    },

    filtersJson() {
      const defaultValue = ''
      if (this.checkEmpty(this.filters) === true) return defaultValue
      return JSON.stringify(this.filters)
    },

    currentPagerKey() {
      if (this.validateIndex() === false) return null
      const current = this.$store.state.postsPager.keys.find((item) => item.index === this.index)
      return current ? current.key : null
    },

    currentParams() {
      let params = {
        sort: this.sort,
        order: this.order,
      }
      if (this.filterCategory) {
        params.category = this.filterCategory
      }
      if (this.currentPagerKey) {
        params.pagerKey = this.currentPagerKey
      }
      return params
    },

    currentParamsForReq() {
      let params = {
        sort: this.sort,
        order: this.order,
      }
      if (this.filterCategory) {
        params.category = this.filterCategory
      }
      if (this.currentPagerKey) {
        params.pagerKey = JSON.stringify(this.currentPagerKey)
      }
      return params
    },

    existsNext() {
      const nextPage = this.index + 1
      return Boolean(this.$store.state.postsPager.keys.find((item) => item.index === nextPage))
    },

    existsPrev() {
      const prevPage = this.index - 1
      return prevPage >= 0
    },

    sortIconClass() {
      const classSuffix = this.order === 'asc' ? 'up' : 'down'
      return `fas fa-caret-${classSuffix}`
    },

    hasFilterInputError() {
      if (this.filterCategoryInputError.length > 0) return true
      return false
    },
  },

  watch: {
    index(val) {
      this.fetchPosts(null, true)
    },

    sort(val) {
      this.$store.dispatch('resetPostsPager', true)
      this.fetchPosts(null, true)
    },

    order(val) {
      this.$store.dispatch('resetPostsPager', true)
      this.fetchPosts(null, true)
    },

    filterCategoryQuery(val) {
      this.filterCategory = this.filterCategoryQuery
      this.$store.dispatch('resetPostsPager', true)
      this.fetchPosts(null, true)
    },

    filterCategory(val) {
      this.isLoaded = false
    },
  },

  async created() {
    this.checkReqIndexAndRedirect()
    this.setCategories()
    if (this.filterCategoryQuery) {
      this.filterCategory = this.filterCategoryQuery
    }
    const isReqLatestPosts = this.$store.getters.postListStored(this.serviceId) === false
    this.fetchPosts(null, isReqLatestPosts)
    if (!this.isAdmin) this.setMasteredPostIds()
  },

  methods: {
    checkReqIndexAndRedirect() {
      if (this.validateIndex() === false) {
        this.$store.dispatch('resetPostsPager', true)
        this.$router.push({
          path: `${this.urlPrefix}/posts/${this.serviceId}`,
          query: this.currentParamsForReq,
        })
      }
    },

    updateSortOrder(sortKey) {
      let order = 'desc'
      if (this.sort === sortKey) {
        order = this.order === 'asc' ? 'desc' : 'asc'
      }

      let paramsCloned = { ...this.currentParamsForReq }
      paramsCloned.sort = sortKey
      paramsCloned.order = order
      this.$router.push({ query: paramsCloned })
    },

    getUrlObjBySortOrder(sortKey) {
      let order = 'desc'
      if (this.sort === sortKey) {
        order = this.order === 'asc' ? 'desc' : 'asc'
      }

      let paramsCloned = { ...this.currentParamsForReq }
      paramsCloned.sort = sortKey
      paramsCloned.order = order
      return {
        path: `${this.urlPrefix}/posts/${this.serviceId}`,
        query: paramsCloned,
      }
    },

    getUrlObjByPageIndex(index = 0) {
      let paramsCloned = { ...this.currentParamsForReq }
      paramsCloned.index = String(index)
      return {
        path: `${this.urlPrefix}/posts/${this.serviceId}`,
        query: paramsCloned,
      }
    },

    async executeFilter() {
      this.validateFilterWithError()
      if (this.hasFilterInputError) return
      if (this.checkFiltersInputChanged() === false) return

      this.$store.dispatch('resetPostsPager', true)
      this.$router.push({ query: this.currentParamsForReq })
    },

    async resetFilter() {
      this.filterCategory = ''
      this.filterCategoryInputError = ''
      const params = {
        sort: this.sort,
        order: this.order,
      }
      if (this.checkFiltersInputChanged() === false) return

      this.$store.dispatch('resetPostsPager', true)
      this.$router.push({ query: params })
    },

    async setCategories() {
      if (this.checkEmpty(this.$store.state.categoryItems) === false) return

      this.$store.dispatch('setLoading', true)
      try {
        const res = await Category.get(this.serviceId, null, { isList: 1 })
        this.$store.dispatch('setCategoryItems', res)
        this.$store.dispatch('setLoading', false)
      } catch (err) {
        this.debugOutput(err)
        this.handleApiError(err, 'Failed to get data from server')
        this.$store.dispatch('setLoading', false)
      }
    },

    async fetchPosts(params = null, isForceUpdate = true) {
      if (this.validateAll() === false) {
        console.log('invalid input')
        this.showGlobalMessage(this.$t('msg.InvalidInput'))
        return
      }

      if (params == null) {
        params = this.currentParamsForReq
      }
      let paramsForApi = { ...params }
      paramsForApi.withCategory = 0

      this.$store.dispatch('setLoading', true)
      try {
        if (isForceUpdate === true || this.checkParamsChanged() === true) {
          // await this.checkAndRefreshTokens()
          const res = await Post.get(this.serviceId, null, paramsForApi)
          this.isLoaded = true
          this.posts = res.items
          // this.$store.dispatch('setPostList', {
          //   items: res.items,
          //   serviceId: this.serviceId,
          // })
          if (res.pagerKey) {
            const nextPagerKey = { index: this.index + 1, key: res.pagerKey }
            this.$store.dispatch('pushItemToPostsPagerKeys', nextPagerKey)
          }
        }
        this.setRequestedParams(params)

        let paramsForStore = { ...this.currentParams }
        paramsForStore.index = this.index
        this.$store.dispatch('setPostsPagerParams', paramsForStore)
        this.$store.dispatch('setLoading', false)
      } catch (err) {
        this.debugOutput(err)
        this.handleApiError(err, 'Failed to get data from server')
        this.$store.dispatch('setLoading', false)
      }
    },

    async setMasteredPostIds(isLatest = false) {
      let params = {
        count: 100,
        order: 'asc',
      }
      this.$store.dispatch('setLoading', true)
      try {
        const items = await User.getPostsMastered(this.serviceId, params, this.userToken)
        items.map((item) => {
          this.masteredPostIds.push(item.postId)
        })
        this.$store.dispatch('setLoading', false)
      } catch (err) {
        this.debugOutput(err)
        this.$store.dispatch('setLoading', false)
        this.handleApiError(err, this.$t('msg["Failed to get data from server"]'))
      }
    },

    checkParamsChanged() {
      let current = { ...this.currentParams }
      current.index = this.index

      const storedParams = this.$store.state.postsPager
      if (current.hasOwnProperty('filters') && current.filters != null) {
        if (storedParams.filters == null) return true
        if (utilObj.isEqual(storedParams.filters, current.filters) === false) return true
      }
      delete current.filters

      const stored = {}
      if (storedParams.sort != null) stored.sort = storedParams.sort
      if (storedParams.order != null) stored.order = storedParams.order
      if (storedParams.category != null) stored.category = storedParams.category
      if (storedParams.lastIndex != null) stored.index = storedParams.lastIndex
      return utilObj.isEqual(stored, current) === false
    },

    checkFiltersInputChanged() {
      const storedParams = this.$store.state.postsPager
      return this.filterCategoryInput != storedParams.category
    },

    validateAll() {
      if (this.sortsAllowed.includes(this.sort) === false) return false
      if (this.ordersAllowed.includes(this.order) === false) return false
      return true
    },

    validateIndex() {
      if (this.index > this.$store.getters.postsPagerIndexCount()) return false
      if (this.index < 0) return false
      return true
    },

    validateFilterWithError() {
      if (this.validateFilterCategory() === false) {
        this.filterCategoryInputError = this.$t('msg.InvalidInput')
      }
    },

    validateFilterCategory() {
      this.filterCategory = this.filterCategory.trim()
      if (!this.filterCategory) return true
      return utilStr.checkSlug(this.filterCategory)
    },

    setRequestedParams(params) {
      this.requestedParams = params
    },
  },
}
</script>

<style lang="scss" scoped>
.master-col {
  width: 100px;
}
</style>
